import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { useModalEscClose } from '../../../utils/hooks/use-modal-esc';
import { useModalFocusTrap } from '../../../utils/hooks/use-modal-focus-trap';
import { CloseIcon } from '../../shared/icons';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: 'small' | 'medium' | 'large' | 'x-large';
  title?: ReactNode;
  caption?: string;
  headerExtra?: ReactNode;
  footerActions?: ReactElement[];
  /** NEW: keep default behavior unless explicitly disabled */
  closeOnOutsideClick?: boolean;
  /** Allow closing modal with ESC key */
  closeOnEsc?: boolean;
  /** Optional: Show confirmation before closing when there's unsaved content */
  confirmBeforeClose?: boolean;
  /** Optional: Custom confirmation message */
  confirmMessage?: string;
  /** Optional: Custom width override (e.g., '720px', '60%') - takes precedence over size */
  customWidth?: string;
  /** Optional: Custom max height override (e.g., '80vh', '600px') */
  customMaxHeight?: string;
}

export default function Modal({
  isOpen,
  onClose,
  children,
  size = 'medium',
  title,
  caption,
  headerExtra,
  footerActions,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  confirmBeforeClose = false,
  confirmMessage = 'You have unsaved changes. Are you sure you want to close?',
  customWidth,
  customMaxHeight
}: Props) {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();

  // Backgrounds for header and footer
  const secondLayer = getColour('secondLayer');
  const headerBg = secondLayer;
  const footerBg = secondLayer;
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationClass, setAnimationClass] = useState('');
  useModalFocusTrap(isOpen, contentRef);

  const handleClose = useCallback(() => {
    // If confirmation is required, show confirm dialog
    if (confirmBeforeClose) {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) return;
    }
    onClose();
  }, [onClose, confirmBeforeClose, confirmMessage]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setAnimationClass('modal-fade-in'), 10);
    } else {
      setAnimationClass('modal-fade-out');
      const timeout = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useModalEscClose(isOpen, handleClose, closeOnEsc);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return 'max-w-sm min-w-[20%] max-h-[70vh]';
      case 'medium':
        return 'max-w-md min-w-[50%] max-h-[80vh]';
      case 'large':
        return 'max-w-lg min-w-[70%] md:mx-20 max-h-[85vh]';
      case 'x-large':
        return 'max-w-5xl min-w-[90%] md:mx-20 max-h-[90vh]';
      default:
        return '';
    }
  };

  const getBottomMargin = () => {
    switch (size) {
      case 'small':
        return 'pb-2';
      case 'medium':
        return 'pb-3';
      case 'large':
        return 'mb-0 pb-0';
      case 'x-large':
        return 'mb-5 pb-5';
      default:
        return 'mb-5 pb-5';
    }
  };

  const overlayStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '0 1rem',
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };

  const contentStyle: React.CSSProperties = {
    position: 'relative',
    border: `1px solid ${getColour('hairline')}`,
    background: getColour('secondLayer'),
    width: customWidth || '100%',
    maxWidth: customWidth || undefined,
    maxHeight: customMaxHeight || undefined,
    borderRadius: '1rem',
    color: getColour('normalText'),
    outline: 'none',
    display: 'flex',
    flexDirection: 'column'
  };

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // If outside-close is disabled, ignore overlay clicks entirely.
    if (!closeOnOutsideClick) return;
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!shouldRender) return null;

  const hasContent =
    children && !(typeof children === 'string' && children.trim() === '');

  return (
    <>
      <div
        style={overlayStyle}
        className={cn('modal-overlay', animationClass)}
        onMouseDown={handleOverlayMouseDown}
      >
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          style={contentStyle}
          className={cn(
            'outline-none flex flex-col shadow-pop',
            !customWidth && getSizeClass()
          )}
          onClick={handleContentClick}
        >
          <div
            className="flex-shrink-0 w-full flex flex-row items-center justify-between gap-x-4 py-4 px-6 border-b rounded-t-2xl"
            style={{ borderColor: getColour('hairline'), background: headerBg }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="min-w-0 flex flex-col">
                {title && (
                  <h2 id={titleId} className="font-semibold text-base">
                    {title}
                  </h2>
                )}
                {caption && (
                  <p
                    className="text-start text-xs truncate"
                    style={{ color: getColour('inactiveText') }}
                    title={caption}
                  >
                    {caption}
                  </p>
                )}
              </div>
              {headerExtra && (
                <>
                  <div
                    className="self-stretch w-px flex-shrink-0"
                    style={{ backgroundColor: getColour('border') }}
                  />
                  {headerExtra}
                </>
              )}
            </div>
            <button
              type="button"
              aria-label={t('close')}
              className="border-none bg-transparent p-0 m-0 flex items-center justify-center hover:text-primary flex-shrink-0"
              style={{ color: getColour('icon') }}
              onClick={handleClose}
            >
              <CloseIcon />
            </button>
          </div>

          {hasContent && (
            <div className="flex-1 min-h-0 overflow-y-auto w-full p-4 flex flex-col">
              <div className={cn(getBottomMargin())}>{children}</div>
            </div>
          )}

          {footerActions && (
            <div
              className="flex-shrink-0 w-full flex flex-wrap justify-end flex-row items-center gap-1 px-6 py-3 border-t rounded-b-2xl"
              style={{
                borderColor: getColour('hairline'),
                background: footerBg
              }}
            >
              {footerActions.map((action, index) => (
                <div key={index}>{action}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .modal-overlay {
          transition: opacity 200ms ease-in-out;
        }
        .modal-fade-in {
          opacity: 1;
        }
        .modal-fade-out {
          opacity: 0;
        }
      `}</style>
    </>
  );
}
